import * as React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { useTheme } from '@mui/material/styles'
import type { CalendarEvent } from '../../typings'
import { resolveEventColor } from '../../eventColors'
import { eventAccessibleName, eventTimeLabel } from '../../eventLabel'
import { useOpenEventDetail } from '../EventDetailPopover/selectionContext'

type Props = {
  event: CalendarEvent
}

/**
 * One row of the Up-next rail: a colour bar + title/time text block. Clicking it
 * opens the detail popover (SH-1986), anchored to the row.
 */
export function UpNextRow({ event }: Props) {
  const theme = useTheme()
  const color = resolveEventColor(event, theme)
  const openDetail = useOpenEventDetail()

  const timeLabel = eventTimeLabel(event)

  return (
    <ButtonBase
      focusRipple
      onClick={(e) => {
        e.stopPropagation()
        openDetail(event, e.currentTarget)
      }}
      aria-label={eventAccessibleName(event)}
      aria-haspopup="dialog"
      data-test-id="up-next-row"
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '10px',
        padding: '8px',
        borderRadius: '6px',
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: '3px',
          flex: '0 0 auto',
          borderRadius: '2px',
          backgroundColor: color.border,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: '13px',
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {event.title}
        </Box>
        <Box
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: '12px',
            fontWeight: 400,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {timeLabel}
        </Box>
      </Box>
    </ButtonBase>
  )
}
